import constants from '@/constants';
import BaseService from './BaseService';

const resource = 'api/v3/sap/?url=/';

class RequestService extends BaseService {
  constructor() {
    super({ resource });
  }

  async getRequests({ filters, id }) {
    try {
      const requestId = id === undefined ? '' : `${id}/`;
      const requestType = (filters?.request_type === undefined) ? null : filters.request_type;
      const status = (filters?.status === undefined) ? null : filters.status;
      const action = `api_scc_cc/request/${requestId}?type=${requestType}&status=${status}`;
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (e) {
      return [];
    }
  }

  async getRequestTypes(onlyRegister) {
    try {
      const action = 'api_scc_cc/request-type/';
      const response = await this.http.get(this.resource + action);

      if (onlyRegister) {
        response.data = response.data.filter((type) => (
          type.id === constants.REQUEST_TYPES.CONTRATISTA.id
        ));
        return response.data;
      } if (onlyRegister === false) {
        return [
          constants.REQUEST_TYPES.RENOVACION_CARNET,
          constants.REQUEST_TYPES.EMISION_CARNET,
          constants.REQUEST_TYPES.ESPORADICO,
          constants.REQUEST_TYPES.REPOSICION_POR_PERDIDA,
        ];
      }
    } catch (err) {
      throw new Error(err.message);
    }
  }

  async getRequestStatuses() {
    try {
      const action = 'api_scc_cc/status/';
      const response = await this.http.get(this.resource + action);
      return response.data.filter((status) => status.solicitud === 'X');
    } catch (err) {
      return err;
    }
  }

  async getRequestIdentificationTypes(onlyIdTypes, includeRnc) {
    try {
      const action = 'api_scc_cc/document-type/';
      const response = await this.http.get(this.resource + action);
      // El Registro Mercantil es un documento de soporte, no un tipo de
      // identificacion, por lo que no debe aparecer en el selector.
      const isIdentificationType = (type) => type.name?.trim().toLowerCase()
        !== 'registro mercantil';
      if (includeRnc && onlyIdTypes) {
        response.data = response.data.filter((type) => type.for_request === 'X'
          && isIdentificationType(type));
      } else if (onlyIdTypes) {
        response.data = response.data.filter((type) => type.for_request === 'X'
          && type.id !== constants.REQUEST_IDENTIFICATION_TYPES.RNC.id
          && isIdentificationType(type));
      }
      return response.data;
    } catch (err) {
      throw new Error(err.message);
    }
  }

  async createRequest(data) {
    const newRequest = {
      address: data.address,
      comment: data.comment,
      email: data.email,
      ext_rej_reason: '',
      birthday: data.birthday,
      passport_exp_date: data.passport_exp_date,
      comprobante_fiscal: data.comprobante_fiscal,
      nationality: {
        id: data.nationality.id ? data.nationality.id : data.nationality.code,
      },
      id: '',
      identification: data.identification,
      identification_type: {
        id: data.identification_type.id,
        name: data.identification_type.name,
      },
      int_rej_reason: '',
      name: data.name,
      last_name: data.last_name,
      phone_number: data.phone_number,
      request_type: {
        id: data.request_type.id,
        name: data.request_type.name,
      },
      state: {
        id: constants.REQUEST_STATUSES.PENDIENTE_PAGO.id,
        name: constants.REQUEST_STATUSES.PENDIENTE_PAGO.name,
      },
      tax_credit: data.tax_credit,
    };

    const action = 'api_scc_cc/request/';
    return this.http.post(this.resource + action, newRequest);
  }

  async updateRequest(id, data, editContractor) {
    let stateUpdate = {};
    if (editContractor) {
      stateUpdate = {
        id: constants.REQUEST_STATUSES.PENDIENTE_PAGO.id,
        name: constants.REQUEST_STATUSES.PENDIENTE_PAGO.name,
      };
    } else {
      stateUpdate = {
        id: constants.REQUEST_STATUSES.VALIDACION_PENDIENTE.id,
        name: constants.REQUEST_STATUSES.VALIDACION_PENDIENTE.name,
      };
    }
    const request = {
      address: data.address,
      comment: data.comment,
      email: data.email,
      ext_rej_reason: data.ext_rej_reason,
      birthday: data.birthday,
      passport_exp_date: data.passport_exp_date,
      factura: data.factura,
      pedido: data.pedido,
      doc_pago: data.doc_pago,
      doc_contable: data.doc_contable,
      nationality: {
        id: data.nationality.id ? data.nationality.id : data.nationality.code,
      },
      id: data.id,
      identification: data.identification,
      identification_type: {
        id: data.identification_type.id,
        name: data.identification_type.name,
      },
      int_rej_reason: data.int_rej_reason,
      name: data.name,
      last_name: data.last_name,
      phone_number: data.phone_number,
      request_type: {
        id: data.request_type.id,
        name: data.request_type.name,
      },
      state: stateUpdate,
      tax_credit: data.tax_credit,
      bp: data.bp,
      bp_new: data.bp_new,
    };

    const action = `api_scc_cc/request/${id}/`;
    return this.http.put(this.resource + action, request);
  }

  async setDocumentRequest(request, files) {
    files[0].forEach((file) => {
      try {
        const data = {
          documents: [file],
          request: request.data,
        };

        const action = `api_scc_cc/request/${request.data.id}/documents/`;
        return this.http.post(this.resource + action, data);
      } catch (err) {
        throw new Error(`Error al subir el documento: ${err.message}`);
      }
    });
  }

  async clearDocumentsCarnetRequest(id) {
    const action = `api_scc_cc/request/card/${id}/documents/delete/`;
    return this.http.post(this.resource + action, {});
  }

  /**
   * Helper to delay execution (for retry backoff)
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Upload document with retry logic and exponential backoff
   * @param {string} action - API endpoint
   * @param {object} form - Request form data
   * @param {object} file - File to upload
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   */
  async uploadDocumentWithRetry(action, form, file, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const data = {
          documents: [file],
          request: form,
        };

        return await this.http.post(this.resource + action, data, {
          timeout: 30000, // 30 second timeout
        });
      } catch (error) {
        lastError = error;

        // Only retry on network errors or 502/503/504 status codes
        const shouldRetry = !error.response
          || [502, 503, 504].includes(error.response?.status);

        if (!shouldRetry || attempt === maxRetries) {
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delayMs = 2 ** attempt * 1000;
        console.warn(`Upload attempt ${attempt} failed, retrying in ${delayMs}ms...`);
        await this.delay(delayMs);
      }
    }

    throw lastError;
  }

  /**
   * Upload documents concurrently with limit and retry logic
   * @param {object} request - Request response object
   * @param {object} files - Files object to upload
   */
  async setDocumentCarnetRequest(request, files) {
    const form = request.data;
    const action = `api_scc_cc/request/card/${form.id}/documents/`;

    // Filter out null files and prepare upload tasks
    const validFiles = Object.values(files).filter((file) => file !== null);

    // Limit concurrent uploads to 3 to avoid overloading the server
    const CONCURRENCY_LIMIT = 3;
    const results = [];

    for (let i = 0; i < validFiles.length; i += CONCURRENCY_LIMIT) {
      const batch = validFiles.slice(i, i + CONCURRENCY_LIMIT);
      const batchPromises = batch.map((file) => this.uploadDocumentWithRetry(action, form, file));

      try {
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      } catch (error) {
        // Re-throw with more context
        throw new Error(`Error al subir los documentos: ${error.message}`);
      }
    }

    return results;
  }

  async getDocumentRequest(id) {
    const action = `api_scc_cc/request/${id}/documents/`;
    return this.http.get(this.resource + action);
  }

  async getRequestCardDocuments(id) {
    try {
      const action = `api_scc_cc/request/card/${id}/documents/`;
      return await this.http.get(this.resource + action);
    } catch (e) {
      return {
        status: 500,
        message: e.message,
      };
    }
  }

  async createRequestHead(form) {
    const action = 'api_scc_cc/request/head/';
    return this.http.post(this.resource + action, form);
  }

  async createRequestEmployee(form) {
    const action = 'api_scc_cc/request/card/';
    return this.http.post(this.resource + action, form);
  }

  async updateRequestEmployee(form, isPayed, isInactivate, isActivate) {
    if (form.request_type.id === constants.REQUEST_TYPES.ESPORADICO.id) {
      form.state = {
        id: constants.REQUEST_STATUSES.VALIDACION_PENDIENTE.id,
        name: constants.REQUEST_STATUSES.VALIDACION_PENDIENTE.name,
      };
    } else if (isPayed) {
      form.state = {
        id: constants.REQUEST_STATUSES.VALIDACION_PENDIENTE.id,
        name: constants.REQUEST_STATUSES.VALIDACION_PENDIENTE.name,
      };
    } else {
      form.state = {
        id: constants.REQUEST_STATUSES.PENDIENTE_PAGO.id,
        name: constants.REQUEST_STATUSES.PENDIENTE_PAGO.name,
      };
    }

    if (isInactivate) {
      form.state = {
        id: constants.REQUEST_STATUSES.INACTIVO.id,
        name: constants.REQUEST_STATUSES.INACTIVO.name,
      };
    } else if (isActivate) {
      form.state = {
        id: constants.REQUEST_STATUSES.RETIRO.id,
        name: constants.REQUEST_STATUSES.RETIRO.name,
      };
    }

    const action = `api_scc_cc/request/card/${form.id}/`;
    return this.http.put(this.resource + action, form);
  }

  // async getRequestHead({ filters, id }) {
  //   try {
  //     const requestId = id === undefined ? '' : `${id}/`;
  //     const requestType = (filters?.request_type === undefined) ? null : filters.request_type;
  //     const status = (filters?.status === undefined) ? null : filters.status;
  //     const action = `api_scc_cc/request/head/${requestId}?type=${requestType}&status=${status}`;
  //     const response = await this.http.get(this.resource + action);
  //     return response.data;
  //   } catch (error) {
  //     return [];
  //   }
  // }

  async getRequestHead({
    filters, id, page, pageSize,
  }) {
    try {
      const requestId = id === undefined ? '' : `${id}/`;
      if (filters === undefined) {
        filters = {
          request_type: null,
          status: null,
        };
      }

      const requestType = () => {
        if (filters?.request_type === null || filters?.request_type?.length === 0) {
          return '';
        }
        params = `${params}type=${filters.request_type}`;
      };

      const status = () => {
        if (filters?.status === null || filters?.status?.length === 0) {
          return '';
        }
        params += `&status=${filters.status}`;
      };

      let params = `${requestId}?type=${requestType()}&status=${status()}`;

      if (page !== undefined) {
        params += `&page=${page}`;
      }

      if (pageSize !== undefined) {
        params += `&page_size=${pageSize}`;
      }

      const action = `api_scc_cc/request/head/${params}`;
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (error) {
      return [];
    }
  }

  async getRequestCardEmployee(id) {
    const action = `api_scc_cc/request/card/${id}/`;
    const response = await this.http.get(this.resource + action);
    return response;
  }

  async getRequestValidities(requestTypeId) {
    const action = 'api_scc_cc/validity-type/';
    const response = await this.http.get(this.resource + action);

    const validityFilters = {
      [constants.REQUEST_TYPES.EMISION_CARNET.id]: (item) => (
        item.id !== constants.VALIDITIES.REIMPRESION_POR_PERDIDA.id
        && item.id !== constants.VALIDITIES.ESPORADICO_5_DIAS.id
        && item.id !== constants.VALIDITIES.ESPORADICO_3_DIAS.id
        && item.id !== constants.VALIDITIES.ESPORADICO_1_DIA.id
      ),
      [constants.REQUEST_TYPES.RENOVACION_CARNET.id]: (item) => (
        item.id !== constants.VALIDITIES.REIMPRESION_POR_PERDIDA.id
        && item.id !== constants.VALIDITIES.ESPORADICO_5_DIAS.id
        && item.id !== constants.VALIDITIES.ESPORADICO_3_DIAS.id
        && item.id !== constants.VALIDITIES.ESPORADICO_1_DIA.id
      ),
      [constants.REQUEST_TYPES.ESPORADICO.id]: (item) => (
        item.id === constants.VALIDITIES.ESPORADICO_5_DIAS.id
        || item.id === constants.VALIDITIES.ESPORADICO_3_DIAS.id
        || item.id === constants.VALIDITIES.ESPORADICO_1_DIA.id
      ),
    };

    const filterFunction = validityFilters[requestTypeId];

    return filterFunction
      ? response.data.filter(filterFunction)
      : response.data.filter((item) => item.id === constants.VALIDITIES.REIMPRESION_POR_PERDIDA.id);
  }

  async getNationalities() {
    const action = 'api_real_state/nationality/';
    try {
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (err) {

    }
  }

  async cancelRequest(requestId) {
    const action = `api_scc_cc/request/card/${requestId}/cancel/`;
    return this.http.post(this.resource + action);
  }

  async cancelCardRequest(requestCardId) {
    // SAP exige el card completo en el PUT; el objeto de la fila de la tabla no
    // trae todos los campos y provoca un dump por referencia nula. Se relee el
    // card y se normaliza igual que la pantalla de edición antes de enviarlo.
    const action = `api_scc_cc/request/card/${requestCardId}/`;
    const { data } = await this.http.get(this.resource + action);

    const form = {
      ...data,
      nationality: { id: data.nationality?.id ?? data.nationality?.code },
      identification: (data.identification ?? '').replaceAll('-', ''),
      birthday: data.birthday || '0000-00-00',
      passport_exp_date: data.passport_exp_date || '0000-00-00',
      state: {
        id: constants.REQUEST_STATUSES.CANCELADO.id,
        name: constants.REQUEST_STATUSES.CANCELADO.name,
      },
    };

    return this.http.put(this.resource + action, form);
  }

  async completeEsporadicoRequest(requestId) {
    const action = `api_scc_cc/request/card/${requestId}/esporadico-complete/`;
    return this.http.post(this.resource + action);
  }

  async deleteCardRequest(requestId, requestCardId) {
    const action = `api_scc_cc//request/card/${requestId}/${requestCardId}/delete/`;
    return this.http.post(this.resource + action);
  }

  async getCarnetTemporary(headId, requestId) {
    const action = `api_scc_cc/request/card/${headId}/${requestId}/carnet/`;
    return this.http.get(this.resource + action);
  }

  async get_exchange_rate() {
    try {
      const action = 'api_portal_clie/dolar_exchange?sap-client=300';
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (err) {
      return err;
    }
  }

  async getDataByIdentification(idNumber) {
    try {
      const action = `api_scc_cc/person/${idNumber}/get-data/?documents=x" `;
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (e) {
      return e.response.status;
    }
  }

  async getDataByPersonIdORIdentification(personId, identification, userId) {
    try {
      const action = `api_scc_cc/request/get-data/?person_id=${personId}&identification=${identification}&user_id=${userId}`;
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (e) {
      return e.response.status;
    }
  }

  async getInvoice(sapCustomer, invoiceId, merchantNumber) {
    try {
      const action = `api_portal_clie/invoice/${sapCustomer}/${invoiceId}/invoice-pdf/?merchant_number=${merchantNumber}&`;
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (e) {
      return e.response.status;
    }
  }

  async getInvoiceMembership(contractorId) {
    try {
      const action = `/api_scc_cc/contractor/${contractorId}/membership/invoices/`;
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (e) {
      return e.response.status;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async getAccessAreas() {
    try {
      const action = 'api_svi/project/?&is_contratista=X';
      const response = await this.http.get(this.resource + action);
      return response.data;
    } catch (e) {
      return e.response.status;
    }
  }

  async compareFaces(files) {
    try {
      const action = 'api/v1/jim-morrinson-tools/compare-documents/';
      const response = await this.http.post(action, files);
      return response.data;
    } catch (e) {
      return e.response.status;
    }
  }

  async validateEsporadico(data) {
    try {
      const action = 'api_scc_cc/request/card/esporadico-validar/';
      const response = await this.http.post(this.resource + action, data);
      return response;
    } catch (e) {
      return e.response;
    }
  }

  async getRequestCardEmployeeByIdentification(identification, userId) {
    let action = '';
    if (userId !== undefined) {
      action = `api_scc_cc/request/get-data/?identification=${identification}&user_id=${userId}`;
    } else {
      action = `api_scc_cc/request/get-data/?identification=${identification}`;
    }
    const response = await this.http.get(this.resource + action);
    return response;
  }

  async inactivateCarnet(requestId) {
    const request = await this.getRequestCardEmployee(requestId);
    // const action = `api_scc_cc/request/card/${requestId}/inactivate/`;
    // return this.http.post(this.resource + action);
  }

  async activateCarnet(requestId) {
    const action = `api_scc_cc/request/card/${requestId}/activate/`;
    return this.http.post(this.resource + action);
  }

  async denunciateCarnet(requestId, data) {
    const action = `api_scc_cc/request/card/${requestId}/complaint/`;
    return this.http.post(this.resource + action, data);
  }

  async createAppointment(data) {
    const action = 'api_scc_cc/schedule?sap-client=300';
    const response = await this.http.post(this.resource + action, data);
    return response;
  }

  async getAppointmentAvailable(data) {
    const action = `api_scc_cc/availability?from=${data.from}&to=${data.to}`;
    const response = await this.http.get(this.resource + action);
    return response;
  }

  async getAppointments(id) {
    let action = 'api_scc_cc/schedule';
    if (id) {
      action += `?id=${id}`;
    }
    const response = await this.http.get(this.resource + action);
    return response;
  }

  async inactivateCarnetRequest(requestId) {
    const action = `api_scc_cc/request/card/${requestId}/inactivate/`;
    return this.http.post(this.resource + action);
  }

  async activateCarnetRequest(requestId) {
    try {
      const action = `api_scc_cc/request/card/${requestId}/inactivate/`;
      return await this.http.post(this.resource + action);
    } catch (error) {
      return error.response;
    }
  }
}

export default RequestService;
